import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { format } from "date-fns";
import { 
  CalendarIcon, 
  ArrowRight, 
  Globe, 
  Package, 
  HeartHandshake, 
  Search, 
  Share2,
  Smartphone,
  Megaphone,
  CheckCircle2,
  Sparkles,
  LogOut,
  Shield,
  User,
  ChevronDown,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_agency-audit-tool/artifacts/aj4ztq87_image.png";

const sections = [
  { id: "discover", name: "DISCOVER", desc: "Website & Product Experience", icon: Globe, color: "#FACC15" },
  { id: "mobile", name: "DISCOVER MOBILE", desc: "Mobile Optimization", icon: Smartphone, color: "#F97316" },
  { id: "drive", name: "DRIVE", desc: "Marketing Readiness", icon: Megaphone, color: "#A855F7" },
  { id: "dominate", name: "DOMINATE", desc: "Search Visibility (SEO)", icon: Search, color: "#3B82F6" },
  { id: "discourse", name: "DISCOURSE", desc: "Social Footprint", icon: Share2, color: "#8B5CF6" },
  { id: "deliver", name: "DELIVER", desc: "Post-Purchase & Packaging", icon: Package, color: "#22C55E" },
  { id: "delight", name: "DELIGHT", desc: "Customer Support", icon: HeartHandshake, color: "#EC4899" },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isSuperAdmin, logout, getGoogleAuthUrl, refreshUser, loading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [date, setDate] = useState(new Date());
  const [loginUsername, setLoginUsername] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);
  const [formData, setFormData] = useState({
    store_name: "",
    store_url: "",
    auditor_name: "",
  });

  // Pre-fill auditor name from user
  useEffect(() => {
    if (user?.name && !formData.auditor_name) {
      setFormData(prev => ({ ...prev, auditor_name: user.name }));
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleGoogleLogin = () => {
    window.location.href = getGoogleAuthUrl();
  };

  const handleCredentialLogin = async (e) => {
    e.preventDefault();
    if (!loginUsername || !loginPassword) return;
    setLoggingIn(true);
    try {
      const resp = await axios.post(`${API}/auth/login`, { username: loginUsername, password: loginPassword });
      if (resp.data.session_token) {
        localStorage.setItem("impress360_session_token", resp.data.session_token);
      }
      await refreshUser();
      toast.success(`Welcome, ${resp.data.name}!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out successfully");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.store_name || !formData.store_url || !formData.auditor_name) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/audits`, {
        client_info: {
          ...formData,
          audit_date: format(date, "yyyy-MM-dd"),
        },
      });
      
      toast.success("Audit created successfully!");
      navigate(`/dashboard/${response.data.id}`);
    } catch (error) {
      console.error("Error creating audit:", error);
      toast.error("Failed to create audit. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="spinner w-10 h-10" />
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-zinc-950 bg-grid relative overflow-hidden flex items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-zinc-900/50 to-zinc-950 pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/5 rounded-full blur-3xl pointer-events-none" />
        
        <div className="relative z-10 w-full max-w-lg px-4">
          <div className="text-center mb-10">
            <img src={LOGO_URL} alt="Digital Impressions" className="h-14 w-auto mx-auto mb-8" />
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-medium">Powered by Digital Impressions</span>
            </div>
            <h1 className="heading-xl text-white mb-4">
              IMPRESS <span className="text-primary">360</span> AUDIT
            </h1>
            <p className="text-lg text-zinc-400 max-w-md mx-auto">
              Comprehensive e-commerce performance audit covering website presence, 
              post-purchase experience, support, SEO, and social media.
            </p>
          </div>
          
          <Card className="card-glass glow-yellow-sm">
            <CardContent className="p-8">
              <h2 className="heading-md text-white text-center mb-2">Sign in to get started</h2>
              <p className="text-zinc-500 text-center text-sm mb-6">
                Use your Google account to access the audit tool
              </p>

              {/* Google Login - Primary */}
              <Button
                onClick={handleGoogleLogin}
                className="w-full h-12 bg-white hover:bg-zinc-100 text-zinc-900 font-medium rounded-xl flex items-center justify-center gap-3"
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>

              {/* Collapsible credentials login - Secondary */}
              <div className="mt-6">
                {!showCredentials ? (
                  <button
                    onClick={() => setShowCredentials(true)}
                    className="w-full text-center text-xs text-zinc-500 hover:text-zinc-400 transition-colors"
                    data-testid="show-credentials-btn"
                  >
                    Sign in with organization credentials
                  </button>
                ) : (
                  <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex-1 h-px bg-zinc-800" />
                      <span className="text-xs text-zinc-500">Organization Login</span>
                      <div className="flex-1 h-px bg-zinc-800" />
                    </div>
                    <form onSubmit={handleCredentialLogin} className="space-y-3">
                      <Input
                        type="text"
                        placeholder="Username"
                        value={loginUsername}
                        onChange={(e) => setLoginUsername(e.target.value)}
                        className="h-10 bg-zinc-900/60 border-zinc-800 text-white placeholder:text-zinc-500 rounded-xl text-sm"
                        data-testid="username-input"
                      />
                      <Input
                        type="password"
                        placeholder="Password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className="h-10 bg-zinc-900/60 border-zinc-800 text-white placeholder:text-zinc-500 rounded-xl text-sm"
                        data-testid="password-input"
                      />
                      <Button
                        type="submit"
                        disabled={loggingIn || !loginUsername || !loginPassword}
                        variant="outline"
                        className="w-full h-10 border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-xl text-sm"
                        data-testid="login-submit-btn"
                      >
                        {loggingIn ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Sign In
                      </Button>
                    </form>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Section previews below login */}
          <div className="mt-10 grid grid-cols-3 gap-3">
            {sections.map((section) => (
              <div
                key={section.id}
                className="flex flex-col items-center gap-2 p-3 rounded-xl bg-zinc-900/40 border border-zinc-800/50"
              >
                <div 
                  className="w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${section.color}20` }}
                >
                  <section.icon className="w-4 h-4" style={{ color: section.color }} />
                </div>
                <span className="text-xs text-zinc-500 text-center leading-tight">{section.name}</span>
              </div>
            ))}
          </div>

          <p className="text-center text-zinc-600 text-sm mt-8">
            Powered by Digital Impressions
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 bg-grid relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-zinc-900/50 to-zinc-950 pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <header className="flex items-center justify-between mb-16">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Digital Impressions" className="h-10 w-auto" />
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              className="text-zinc-400 hover:text-white"
              onClick={() => navigate("/audits")}
              data-testid="view-audits-btn"
            >
              View Saved Audits
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 text-zinc-300 hover:text-white">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                  )}
                  <span className="hidden sm:inline">{user?.name?.split(' ')[0]}</span>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-zinc-900 border-zinc-800">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-white">{user?.name}</p>
                  <p className="text-xs text-zinc-500">{user?.email}</p>
                  {isSuperAdmin && (
                    <span className="inline-flex items-center gap-1 mt-1 text-xs text-primary">
                      <Shield className="w-3 h-3" /> Super Admin
                    </span>
                  )}
                </div>
                <DropdownMenuSeparator className="bg-zinc-800" />
                {isSuperAdmin && (
                  <DropdownMenuItem 
                    onClick={() => navigate("/admin")}
                    className="text-zinc-300 focus:bg-zinc-800 cursor-pointer"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-zinc-300 focus:bg-zinc-800 cursor-pointer"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Form */}
          <div className="animate-fade-in">
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm text-primary font-medium">Powered by Digital Impressions</span>
              </div>
              <h1 className="heading-xl text-white mb-4">
                IMPRESS <span className="text-primary">360</span> AUDIT
              </h1>
              <p className="text-lg text-zinc-400">
                Comprehensive e-commerce performance audit covering website presence, 
                post-purchase experience, support, SEO, and social media.
              </p>
            </div>

            <Card className="card-glass p-6 glow-yellow-sm" data-testid="audit-form-card">
              <CardContent className="p-0">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="store_name" className="text-zinc-300">Client / Store Name</Label>
                    <Input
                      id="store_name"
                      name="store_name"
                      placeholder="e.g., Acme Store"
                      value={formData.store_name}
                      onChange={handleInputChange}
                      className="input-dark h-12"
                      data-testid="store-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="store_url" className="text-zinc-300">Website URL</Label>
                    <Input
                      id="store_url"
                      name="store_url"
                      placeholder="e.g., https://acmestore.com"
                      value={formData.store_url}
                      onChange={handleInputChange}
                      className="input-dark h-12"
                      data-testid="store-url-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="auditor_name" className="text-zinc-300">Auditor Name</Label>
                    <Input
                      id="auditor_name"
                      name="auditor_name"
                      placeholder="e.g., John Doe"
                      value={formData.auditor_name}
                      onChange={handleInputChange}
                      className="input-dark h-12"
                      data-testid="auditor-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-zinc-300">Audit Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal h-12 input-dark",
                            !date && "text-zinc-500"
                          )}
                          data-testid="audit-date-btn"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? format(date, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          initialFocus
                          className="bg-zinc-900"
                          data-testid="audit-date-calendar"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full h-12 btn-primary text-base"
                    data-testid="start-audit-btn"
                  >
                    {isLoading ? (
                      <div className="spinner w-5 h-5" />
                    ) : (
                      <>
                        Start Audit
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Right: Sections Preview */}
          <div className="hidden lg:block">
            <div className="space-y-4">
              <h2 className="heading-md text-zinc-300 mb-6">6 Pillars of Excellence</h2>
              {sections.map((section, index) => (
                <div
                  key={section.id}
                  className={cn(
                    "flex items-center gap-4 p-4 rounded-xl bg-zinc-900/50 border border-zinc-800",
                    "animate-slide-up opacity-0",
                    `stagger-${index + 1}`
                  )}
                  style={{ animationFillMode: "forwards" }}
                  data-testid={`section-preview-${section.id}`}
                >
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${section.color}20` }}
                  >
                    <section.icon className="w-6 h-6" style={{ color: section.color }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                      {section.name}
                    </h3>
                    <p className="text-sm text-zinc-500">{section.desc}</p>
                  </div>
                  <CheckCircle2 className="w-5 h-5 text-zinc-700" />
                </div>
              ))}
            </div>

            {/* Grading Scale Preview */}
            <div className="mt-8 p-6 rounded-xl bg-zinc-900/30 border border-zinc-800">
              <h3 className="heading-sm text-zinc-300 mb-4">Grading Scale</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-sm text-zinc-400">90-100% Excellent</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <span className="text-sm text-zinc-400">75-89% Good</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-orange-500" />
                  <span className="text-sm text-zinc-400">60-74% Needs Work</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span className="text-sm text-zinc-400">&lt;60% Major Issues</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
